/* =====================================================================
 * Input — 공통 인풋 컴포넌트 (테마 기반)
 * ===================================================================== */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Search } from 'lucide-react';

type InputVariant = 'default' | 'search' | 'ghost';
type InputSize = 'sm' | 'md';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: InputVariant;
  inputSize?: InputSize;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ variant = 'default', inputSize = 'md', fullWidth = true, leftIcon, error, className, ...props }, ref) => {
    if (variant === 'search') {
      return (
        <SearchWrapper className={className} $size={inputSize} $fullWidth={fullWidth} $error={error}>
          {leftIcon || <Search size={14} />}
          <StyledGhostInput ref={ref} $size={inputSize} {...props} />
        </SearchWrapper>
      );
    }

    if (variant === 'ghost') {
      return (
        <GhostWrapper className={className} $fullWidth={fullWidth}>
          {leftIcon && <IconSlot>{leftIcon}</IconSlot>}
          <StyledGhostInput ref={ref} $size={inputSize} {...props} />
        </GhostWrapper>
      );
    }

    return (
      <Wrapper className={className} $fullWidth={fullWidth}>
        {leftIcon && <IconSlot>{leftIcon}</IconSlot>}
        <StyledInput ref={ref} $size={inputSize} $hasIcon={!!leftIcon} $fullWidth={fullWidth} $error={error} {...props} />
      </Wrapper>
    );
  },
);

Input.displayName = 'Input';

/* ---- Styles ---- */

const Wrapper = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}
`;

const IconSlot = styled.span`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`;

const StyledInput = styled.input<{
  $size: InputSize;
  $hasIcon: boolean;
  $fullWidth: boolean;
  $error?: boolean;
}>`
  border: 1px solid ${({ theme, $error }) => ($error ? theme.colors.error : theme.colors.borderInput)};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  outline: none;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}

  ${({ $size, $hasIcon }) =>
    $size === 'sm'
      ? css`
          padding: 6px ${$hasIcon ? '10px 6px 32px' : '10px'};
          font-size: 0.8125rem;
          height: 34px;
        `
      : css`
          padding: 9px ${$hasIcon ? '12px 9px 36px' : '12px'};
          font-size: 0.8125rem;
          height: 40px;
        `}

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchWrapper = styled.div<{
  $size: InputSize;
  $fullWidth: boolean;
  $error?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid ${({ theme, $error }) => ($error ? theme.colors.error : theme.colors.border)};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}

  ${({ $size }) =>
    $size === 'sm'
      ? css`padding: 7px 10px;`
      : css`padding: 10px 12px;`}

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.borderInput};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 0 0 2px rgba(236,236,236,0.1)'
        : '0 0 0 2px rgba(13,13,13,0.08)'};
  }
`;

const StyledGhostInput = styled.input<{ $size: InputSize }>`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  min-width: 0;

  ${({ $size }) =>
    $size === 'sm'
      ? css`font-size: 0.75rem;`
      : css`font-size: 0.8125rem;`}

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const GhostWrapper = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}
`;
